// PrivateRoute.js - Componente para proteger rutas que requieren autenticación
import React from 'react';
import { useAuth } from './AuthContext';
import LoginScreen from './LoginScreen';

// Pantalla de carga mientras se verifica la sesión. Misma identidad que el login
// (gris papel con grano + logo + spinner sutil) para que el paso login → carga →
// app sea una transición continua, sin el salto de color de antes.
const AuthLoadingScreen = () => {
  return (
    <div className="auth-loading-container">
      <div className="auth-loading-content">
        <img
          src={`${process.env.PUBLIC_URL}/brand-logo-full.png`}
          alt="Jonathanva"
          className="auth-loading-logo"
        />
        <div className="auth-loading-spinner" />
        <span className="auth-loading-text">Cargando</span>
      </div>

      <style>{`
        .auth-loading-container {
          position: fixed;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          font-family: var(--font-sans);
          /* Idéntico al login: gris papel con grano SVG sutil. */
          background-color: #ececea;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='240' height='240'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.07'/%3E%3C/svg%3E");
          animation: authfade 0.25s ease-out;
        }

        .auth-loading-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.25rem;
        }

        .auth-loading-logo {
          height: 44px;
          width: auto;
          max-width: calc(100vw - 48px);
          object-fit: contain;
          opacity: 0.95;
        }

        .auth-loading-spinner {
          width: 28px;
          height: 28px;
          border: 2.5px solid rgba(17, 17, 17, 0.12);
          border-top-color: #111111;
          border-radius: 50%;
          animation: authspin 0.8s linear infinite;
        }

        .auth-loading-text {
          font-size: 0.75rem;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #757575;
        }

        @keyframes authspin { to { transform: rotate(360deg); } }
        @keyframes authfade { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </div>
  );
};

// Componente principal PrivateRoute
const PrivateRoute = ({ children, requiredRole = null, fallback = null }) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  // Mostrar loading mientras se verifica la autenticación
  if (isLoading) {
    return <AuthLoadingScreen />;
  }

  // Si no está autenticado, mostrar LoginScreen
  if (!isAuthenticated || !user) {
    return <LoginScreen />;
  }

  // Verificar rol específico si se requiere
  if (requiredRole) {
    const hasRequiredRole = Array.isArray(requiredRole) 
      ? requiredRole.includes(user.rol)
      : user.rol === requiredRole;

    if (!hasRequiredRole) {
      // Si se proporciona un fallback, usarlo
      if (fallback) {
        return fallback;
      }
      
      // Si no, mostrar mensaje de acceso denegado
      return <AccessDeniedScreen userRole={user.rol} requiredRole={requiredRole} />;
    }
  }

  // Usuario autenticado y con permisos correctos
  return children;
};

// Componente para mostrar cuando no tiene permisos
const AccessDeniedScreen = ({ userRole, requiredRole }) => {
  const { logout } = useAuth();

  const handleLogout = () => {
    logout();
  };

  const roleNames = {
    admin: 'Administrador',
    cliente: 'Cliente'
  };

  const getRequiredRoleText = () => {
    if (Array.isArray(requiredRole)) {
      return requiredRole.map(role => roleNames[role] || role).join(' o ');
    }
    return roleNames[requiredRole] || requiredRole;
  };

  return (
    <div className="access-denied-container">
      <div className="access-denied-content">
        <div className="access-denied-icon">🔒</div>
        <h1>Acceso Denegado</h1>
        <p className="access-denied-message">
          No tienes permisos para acceder a esta sección.
        </p>
        <div className="access-denied-details">
          <p><strong>Tu rol actual:</strong> {roleNames[userRole] || userRole}</p>
          <p><strong>Rol requerido:</strong> {getRequiredRoleText()}</p>
        </div>
        <div className="access-denied-actions">
          <button 
            onClick={() => window.history.back()} 
            className="btn-back"
          >
            Volver Atrás
          </button>
          <button 
            onClick={handleLogout} 
            className="btn-logout"
          >
            Cerrar Sesión
          </button>
        </div>
      </div>

      <style>{`
        .access-denied-container {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          /* Móvil: en 320x568 la tarjeta no cabe en alto; se desplaza */
          overflow-y: auto;
        }
        
        .access-denied-content {
          background: white;
          border-radius: 16px;
          padding: 3rem 2rem;
          text-align: center;
          max-width: 500px;
          width: 100%;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }
        
        .access-denied-icon {
          font-size: 4rem;
          margin-bottom: 1.5rem;
          opacity: 0.8;
        }
        
        .access-denied-content h1 {
          font-size: 2rem;
          font-weight: 700;
          color: #1f2937;
          margin: 0 0 1rem 0;
        }
        
        .access-denied-message {
          color: #6b7280;
          font-size: 1.125rem;
          margin: 0 0 2rem 0;
          line-height: 1.6;
        }
        
        .access-denied-details {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 1.5rem;
          margin-bottom: 2rem;
          text-align: left;
        }
        
        .access-denied-details p {
          margin: 0.5rem 0;
          color: #4b5563;
          font-size: 0.875rem;
        }
        
        .access-denied-details p:first-child {
          margin-top: 0;
        }
        
        .access-denied-details p:last-child {
          margin-bottom: 0;
        }
        
        .access-denied-actions {
          display: flex;
          gap: 1rem;
          justify-content: center;
        }
        
        .btn-back,
        .btn-logout {
          padding: 0.75rem 1.5rem;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .btn-back {
          background: #f3f4f6;
          color: #374151;
        }
        
        .btn-back:hover {
          background: #e5e7eb;
        }
        
        .btn-logout {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
        }
        
        .btn-logout:hover {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          transform: translateY(-1px);
        }
        
        @media (max-width: 640px) {
          .access-denied-container {
            align-items: flex-start;
            padding: calc(16px + var(--safe-top)) 16px calc(16px + var(--safe-bottom));
          }

          .access-denied-content {
            padding: 2rem 1rem;
            margin: auto 0;
          }

          .access-denied-content h1 {
            font-size: 1.5rem;
          }

          .btn-back,
          .btn-logout {
            min-height: 44px;
          }
          
          .access-denied-actions {
            flex-direction: column;
          }
          
          .btn-back,
          .btn-logout {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

// Hook para usar en componentes que necesitan verificar roles
export const useRequireAuth = (requiredRole = null) => {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated || !user) {
    return { hasAccess: false, reason: 'not_authenticated' };
  }

  if (requiredRole) {
    const hasRequiredRole = Array.isArray(requiredRole) 
      ? requiredRole.includes(user.rol)
      : user.rol === requiredRole;

    if (!hasRequiredRole) {
      return { 
        hasAccess: false, 
        reason: 'insufficient_permissions',
        userRole: user.rol,
        requiredRole 
      };
    }
  }

  return { hasAccess: true, user };
};

// Componente de conveniencia para proteger solo rutas de admin
export const AdminRoute = ({ children, fallback = null }) => {
  return (
    <PrivateRoute requiredRole="admin" fallback={fallback}>
      {children}
    </PrivateRoute>
  );
};

// Componente de conveniencia para rutas que requieren cualquier rol autenticado
export const AuthenticatedRoute = ({ children }) => {
  return (
    <PrivateRoute>
      {children}
    </PrivateRoute>
  );
};

export default PrivateRoute;